import { React, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import Header from './components/Header/Header';
import Licitatii from './pages/Licitatii/Licitatii';
import AuctionDetailsRouter from './pages/AuctionDetails/AuctionDetailsRouter';

import DirectAwards from './pages/DirectAwards/DirectAwards';
import DirectAwardDetails from './pages/DirectAwardDetails/DirectAwardDetails';
import PriorInformation from './pages/PriorInformation/PriorInformation';
import PriorInformationDetails from './pages/PriorInformationDetails/PriorInformationDetails';

import ContractAwardNotices from './pages/ContractAwardNotices/ContractAwardNotices';
import NoticeDetailsRouter from './pages/ContractAwardNotices/NoticeDetailsRouter';

import Footer from './components/Footer/Footer';
import NotFoundComponent from './components/NotFoundComponent';
import CookiesPolicy from './components/CookiesPolicy';
import Home from './pages/Home/Home';
import Projects from './pages/Projects/Projects';
import ProjectDetail from './pages/ProjectDetail/ProjectDetail';
import Subscriptions from './pages/Subscriptions/Subscriptions';
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import UserAccount from './pages/UserAccount/UserAccount';
import WikiPage from './pages/Wiki/WikiPage';
import Contact from './pages/Contact/Contact';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import ConfirmEmail from './pages/ConfirmEmail/ConfirmEmail';
import Success from './pages/Success/Success';
import OnboardingSuccess from './pages/OnboardingSuccess/OnboardingSuccess';
import RouteLogger from './components/RouteLogger';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import NetworkStatus from './components/NetworkStatus/NetworkStatus';
import CSPConfig from './components/CSPConfig/CSPConfig';
import SolicitaOferta from './pages/SolicitaOferta/SolicitaOferta';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './context/AuthContext';
import { SavedProceduresProvider } from './context/SavedProceduresContext';
import SavedProcedures from './pages/SavedProcedures/SavedProcedures';
import SubscriptionWall from './components/SubscriptionWall/SubscriptionWall';
import LegacyRedirect from './components/LegacyRedirect';
import PaymentStatusBanner from './components/PaymentStatusBanner/PaymentStatusBanner';
import VersionBanner from './components/VersionBanner/VersionBanner';
import AdminLayout from './pages/Admin/AdminLayout';
import AdminOverview from './pages/Admin/AdminOverview';
import AdminUsers from './pages/Admin/AdminUsers';
import AdminLeads from './pages/Admin/AdminLeads';
import AdminAudit from './pages/Admin/AdminAudit';

const App = () => {
  const isDevelopment = import.meta.env.VITE_PRODUCTION === 'development';
  const [, setIsAuthenticated] = useState(false);
  // Check if token exists on mount
  useEffect(() => {
    const token = Cookies.get('jwtToken');
    if (token) {
      setIsAuthenticated(true); // Token exists, user is authenticated
    }
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <SavedProceduresProvider>
        <HelmetProvider>
          <CSPConfig />
          <Router>
            <ScrollToTop />
            <RouteLogger enabled={!isDevelopment} />
            <NetworkStatus />
            <Header />
            <PaymentStatusBanner />
            <VersionBanner />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/licitatii/" element={<Licitatii />} />
                <Route path="/licitatii/:slug/:noticeNo" element={<AuctionDetailsRouter />} />
                <Route path="/licitatii/:noticeNo" element={<LegacyRedirect basePath="/licitatii" />} />

                <Route path="/achizitii-directe/" element={<DirectAwards />} />
                <Route path="/achizitii-directe/:slug/:noticeNo" element={<DirectAwardDetails />} />
                <Route path="/achizitii-directe/:noticeNo" element={<LegacyRedirect basePath="/achizitii-directe" />} />

                <Route path="/anunturi-intentie/" element={<PriorInformation />} />
                <Route path="/anunturi-intentie/:slug/:noticeNo" element={<PriorInformationDetails />} />
                <Route path="/anunturi-intentie/:noticeNo" element={<LegacyRedirect basePath="/anunturi-intentie" />} />

                <Route path="/anunturi-atribuire/" element={<ContractAwardNotices />} />
                <Route path="/anunturi-atribuire/:slug/:noticeNo" element={<NoticeDetailsRouter />} />
                <Route path="/anunturi-atribuire/:noticeNo" element={<LegacyRedirect basePath="/anunturi-atribuire" />} />
                <Route path="/proiecte/" element={<Projects />} />
                <Route path="/proiecte/:slug/:id" element={<ProjectDetail />} />
                <Route path="/proiecte/:id" element={<LegacyRedirect basePath="/proiecte" />} />
                <Route
                  path="/abonamente/"
                  element={
                    <Subscriptions setIsAuthenticated={setIsAuthenticated} />
                  }
                />
                <Route path="/cumpara-credite/" element={<Navigate to="/abonamente" replace />} />
                <Route
                  path="/solicita-oferta/:procedureId/:utm_source"
                  element={<SolicitaOferta />}
                />

                <Route path="/proceduri-salvate" element={<SubscriptionWall><SavedProcedures /></SubscriptionWall>} />
                <Route path="/login/" element={<Login />} />
                <Route path="/register/" element={<Register />} />
                <Route
                  path="/account"
                  element={
                    <UserAccount setIsAuthenticated={setIsAuthenticated} />
                  }
                />
                <Route
                  path="/account/:tab"
                  element={
                    <UserAccount setIsAuthenticated={setIsAuthenticated} />
                  }
                />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminOverview />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="leads" element={<AdminLeads />} />
                  <Route path="audit" element={<AdminAudit />} />
                </Route>
                <Route path="/success" element={<Success />} />
                <Route path="/onboarding/success" element={<OnboardingSuccess />} />
                <Route path="/wiki" element={<WikiPage />} />
                <Route path="/wiki/:articleId" element={<WikiPage />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/confirm-email" element={<ConfirmEmail />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/404" element={<NotFoundComponent />} />
                <Route path="*" element={<NotFoundComponent />} />
              </Routes>
              <div className="p-4">
                <CookiesPolicy />
              </div>
            </main>
            <Footer />
          </Router>
        </HelmetProvider>
        </SavedProceduresProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
